import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  ArrowLeft, Download, Loader2, Send, FileText, ReceiptText, FileSignature,
  Plus, Trash2, MessageSquare, CheckCircle2, CreditCard, Paperclip, Lock, AlertTriangle, Flag,
  Award, FileCheck2, QrCode, Upload,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SERVICE_MAP, STATUS_META, FIELD_LABELS } from "@/data/services";
import { DeadlineBanner } from "@/components/DeadlineWatch";

const STATUSES = ["submitted", "quoted", "paid", "processing", "completed", "cancelled"];
const OUTCOMES = [
  { key: "approved", label: "Approved" },
  { key: "conditionally_approved", label: "Conditionally Approved" },
  { key: "additional_documents", label: "Additional Documents Required" },
  { key: "rejected", label: "Rejected" },
  { key: "completed", label: "Completed" },
];
const OUTCOME_LABEL = Object.fromEntries(OUTCOMES.map((o) => [o.key, o.label]));
const OUTCOME_CLS = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-600 text-white border-emerald-700",
  conditionally_approved: "bg-blue-50 text-blue-700 border-blue-200",
  additional_documents: "bg-amber-50 text-brand-amber border-amber-200",
  rejected: "bg-red-50 text-brand-red border-red-200",
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.submitted;
  return <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${m.cls}`}>{m.label}</span>;
};

const FLOW = [
  { key: "submitted", label: "Submitted", icon: FileText },
  { key: "quoted", label: "Quotation", icon: FileSignature },
  { key: "paid", label: "Paid", icon: CreditCard },
  { key: "processing", label: "Processing", icon: Loader2 },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
];

const Timeline = ({ status }) => {
  if (status === "cancelled") {
    return (
      <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-brand-red" data-testid="status-timeline">
        This application has been cancelled. Please contact our team if you have any questions.
      </div>
    );
  }
  const idx = FLOW.findIndex((s) => s.key === status);
  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 overflow-x-auto" data-testid="status-timeline">
      <div className="flex items-start min-w-[540px]">
        {FLOW.map((s, i) => {
          const reached = i <= idx;
          const active = i === idx;
          const Icon = s.icon;
          return (
            <div key={s.key} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: i * 0.08 }}
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${reached ? "bg-brand-red border-brand-red text-white shadow-redglow" : "bg-white border-slate-200 text-slate-400"}`}>
                  <Icon className={`w-4 h-4 ${active ? "animate-pulse" : ""}`} />
                </motion.div>
                <span className={`mt-2 text-xs font-semibold whitespace-nowrap ${reached ? "text-brand-navy" : "text-slate-400"}`}>{s.label}</span>
              </div>
              {i < FLOW.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 -mt-7 rounded-full bg-slate-200 overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: i < idx ? "100%" : "0%" }} transition={{ duration: 0.5, delay: i * 0.08 }} className="h-full bg-brand-red" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DownloadBtn = ({ href, label, icon: Icon, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm font-semibold text-brand-navy hover:border-brand-red hover:text-brand-red transition-colors">
    <Icon className="w-4 h-4" /> {label} <Download className="w-3.5 h-3.5 opacity-60" />
  </a>
);

export default function ApplicationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [app, setApp] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState([{ description: "Service fee", amount: "" }]);
  const [notes, setNotes] = useState("");
  const [savingQuote, setSavingQuote] = useState(false);
  const [adminNotes, setAdminNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [flagging, setFlagging] = useState(false);
  const [payTab, setPayTab] = useState("paypal");
  const [qrProof, setQrProof] = useState(null);
  const [qrNote, setQrNote] = useState("");
  const [uploadingProof, setUploadingProof] = useState(false);
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [confirmingQr, setConfirmingQr] = useState(false);
  const [resultForm, setResultForm] = useState({ outcome: "approved", summary: "" });
  const [resultFile, setResultFile] = useState(null);
  const [issuingResult, setIssuingResult] = useState(false);
  const [uploadingResult, setUploadingResult] = useState(false);
  const proofRef = useRef(null);
  const resultRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/applications/${id}`);
      setApp(data);
      const m = await api.get(`/applications/${id}/messages`);
      setMessages(m.data);
      if (user?.role === "admin") {
        try { const n = await api.get(`/applications/${id}/admin-notes`); setAdminNotes(n.data); } catch { /* ignore */ }
      }
    } catch (e) { toast.error(apiErr(e)); }
  }, [id, user]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const { data } = await api.post(`/applications/${id}/admin-notes`, { text: noteText });
      setAdminNotes((n) => [...n, data]);
      setNoteText("");
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/applications/${id}/messages`, { text });
      setMessages((m) => [...m, data]); setText("");
    } catch (err) { toast.error(apiErr(err)); } finally { setSending(false); }
  };

  const saveQuote = async () => {
    const clean = items.filter((i) => i.description && i.amount !== "").map((i) => ({ description: i.description, amount: parseFloat(i.amount) || 0 }));
    if (!clean.length) { toast.error("Add at least one line item with an amount."); return; }
    setSavingQuote(true);
    try {
      await api.post(`/applications/${id}/quotation`, { items: clean, notes });
      toast.success("Quotation issued", { description: "The applicant can now review and pay." });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingQuote(false); }
  };

  const prefillOverstay = () => {
    const fee = app?.form_data?.overstay_fee_usd;
    const days = app?.form_data?.overstay_days;
    setItems([
      { description: `Estimated overstay penalty (${days || "?"} days @ $0.50/day)`, amount: fee || "" },
      { description: "ImmiLaw.VN professional service fee", amount: "" },
    ]);
    toast.success("Overstay fee pre-filled — adjust the service fee and issue.");
  };

  const setStatus = async (status) => {
    try { await api.post(`/applications/${id}/status`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const toggleFlag = async () => {
    setFlagging(true);
    const next = !app.needs_action;
    try {
      await api.post(`/applications/${id}/flag`, { needs_action: next });
      setApp((a) => ({ ...a, needs_action: next }));
      toast.success(next ? "Flagged as action needed" : "Flag cleared");
    } catch (e) { toast.error(apiErr(e)); } finally { setFlagging(false); }
  };

  const uploadTo = async (file) => {
    const body = new FormData(); body.append("file", file);
    const { data } = await api.post("/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
    return { path: data.path, filename: data.filename };
  };

  const onProofSelected = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingProof(true);
    try { const f = await uploadTo(file); setQrProof(f); toast.success("Proof attached"); }
    catch (err) { toast.error(apiErr(err)); } finally { setUploadingProof(false); }
  };

  const submitClaim = async () => {
    if (!qrProof) { toast.error("Please upload your payment confirmation first."); return; }
    setSubmittingClaim(true);
    try {
      await api.post(`/applications/${id}/payment-claim`, { proof_path: qrProof.path, proof_filename: qrProof.filename, note: qrNote });
      toast.success("Payment proof submitted", { description: "Our team will confirm your payment shortly." });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSubmittingClaim(false); }
  };

  const confirmQr = async () => {
    setConfirmingQr(true);
    try { await api.post(`/applications/${id}/confirm-qr-payment`); toast.success("Payment confirmed & receipt emailed"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setConfirmingQr(false); }
  };

  const onResultFileSelected = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingResult(true);
    try { const f = await uploadTo(file); setResultFile(f); toast.success("Document attached"); }
    catch (err) { toast.error(apiErr(err)); } finally { setUploadingResult(false); }
  };

  const issueResult = async () => {
    setIssuingResult(true);
    try {
      await api.post(`/applications/${id}/result`, {
        outcome: resultForm.outcome, summary: resultForm.summary,
        result_file_path: resultFile?.path || null, result_filename: resultFile?.filename || null,
      });
      toast.success("Result issued", { description: "The applicant has been emailed the result." });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setIssuingResult(false); }
  };

  useEffect(() => {
    if (app?.result) {
      setResultForm({ outcome: app.result.outcome || "approved", summary: app.result.summary || "" });
      if (app.result.result_file_path) setResultFile({ path: app.result.result_file_path, filename: app.result.result_filename });
    }
  }, [app?.result]);

  const onPaid = async (details) => {
    try {
      await api.post(`/applications/${id}/pay`, {
        provider_txn_id: details?.id || "paypal", payer_email: details?.payer?.email_address || "",
        amount: parseFloat(app.quotation.amount),
      });
      toast.success("Payment successful!", { description: "Your receipt has been emailed to you." });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!app) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-brand-red animate-spin" /></div>;

  const svc = SERVICE_MAP[app.service_type];
  const fd = app.form_data || {};
  const backTo = isAdmin ? "/admin/applications" : "/dashboard";
  const groupedDocs = Array.isArray(fd.documents) ? fd.documents.filter((d) => d && d.label) : [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <Link to={backTo} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-red transition-colors mb-6"><ArrowLeft className="w-4 h-4" /> Back</Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-serif text-4xl text-brand-navy tracking-tight">{svc?.title || app.service_type}</h1>
            <StatusPill status={app.status} />
            {app.needs_action && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-brand-red border border-red-200" data-testid="detail-action-badge">
                <AlertTriangle className="w-3.5 h-3.5" /> Action needed
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 font-mono mt-1">{app.reference_no}</p>
          {isAdmin && <p className="text-sm text-slate-600 mt-1">Applicant: <strong>{app.applicant_name}</strong> · {app.applicant_email}</p>}
        </div>
      </div>

      {/* Documents */}
      <div className="mt-6 flex flex-wrap gap-3" data-testid="documents-bar">
        <DownloadBtn href={fileUrl(`applications/${id}/form.pdf`)} label="Application form" icon={FileText} testid="download-form-btn" />
        {app.quotation && <DownloadBtn href={fileUrl(`applications/${id}/quotation.pdf`)} label="Quotation" icon={FileSignature} testid="download-quotation-btn" />}
        {app.receipt && <DownloadBtn href={fileUrl(`applications/${id}/receipt.pdf`)} label="Receipt" icon={ReceiptText} testid="download-receipt-btn" />}
      </div>

      <Timeline status={app.status} />
      <DeadlineBanner app={app} />

      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        {/* Left: details + payment/quote */}
        <div className="lg:col-span-2 space-y-8">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 font-semibold text-brand-navy">Application details</div>
            <dl className="divide-y divide-slate-100">
              {Object.entries(fd).filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="px-6 py-3 grid grid-cols-2 gap-4 text-sm">
                  <dt className="text-slate-500">{FIELD_LABELS[k] || k.replace(/_/g, " ")}</dt>
                  <dd className="text-brand-navy font-medium break-words">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </div>

          {groupedDocs.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="uploaded-documents">
              <div className="px-6 py-4 border-b border-slate-100 font-semibold text-brand-navy flex items-center gap-2"><Paperclip className="w-4 h-4 text-brand-red" /> {isAdmin ? "Applicant documents by checklist item" : "Uploaded documents"}</div>
              <div className="divide-y divide-slate-100">
                {groupedDocs.map((d, i) => (
                  <div key={i} className="px-6 py-3.5" data-testid={`doc-group-${i}`}>
                    <p className="text-xs uppercase tracking-wide text-slate-400 font-bold mb-1.5">Checklist item {i + 1}</p>
                    <p className="text-sm font-medium text-brand-navy mb-2">{d.label}</p>
                    {d.path ? (
                      <a href={fileUrl(`files/${d.path}`)} target="_blank" rel="noopener noreferrer" data-testid={`uploaded-doc-${i}`}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:border-brand-red hover:text-brand-red transition-colors max-w-full">
                        <FileText className="w-4 h-4 shrink-0 text-brand-red" />
                        <span className="text-xs text-brand-navy truncate max-w-[300px]">{d.filename || "View file"}</span>
                        <Download className="w-3.5 h-3.5 opacity-60 shrink-0" />
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400" data-testid={`doc-missing-${i}`}>
                        <AlertTriangle className="w-3.5 h-3.5" /> Not uploaded
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quotation view */}
          {app.quotation && (
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="quotation-panel">
              <div className="px-6 py-4 border-b border-slate-100 font-semibold text-brand-navy flex items-center justify-between">
                <span>Quotation {app.quotation.quotation_no}</span>
                <span className="text-brand-red font-serif text-2xl">USD {Number(app.quotation.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="px-6 py-3">
                {app.quotation.items.map((it, i) => (
                  <div key={i} className="flex justify-between py-1.5 text-sm border-b border-slate-50 last:border-0">
                    <span className="text-slate-600">{it.description}</span>
                    <span className="text-brand-navy font-medium">{Number(it.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
                {app.quotation.notes && <p className="text-xs text-slate-400 mt-3">{app.quotation.notes}</p>}
              </div>

              {/* Payment for user */}
              {!isAdmin && !app.payment && (
                <div className="px-6 py-5 bg-slate-50 border-t border-slate-100" data-testid="payment-panel">
                  {app.payment_claim?.status === "pending" ? (
                    <div data-testid="qr-claim-pending" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                      <p className="font-semibold flex items-center gap-2"><Paperclip className="w-4 h-4" /> Payment proof submitted</p>
                      <p className="mt-1">We've received your QR payment proof and our team is confirming it. Your receipt will be emailed once confirmed.</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2 mb-4">
                        <button onClick={() => setPayTab("paypal")} data-testid="pay-tab-paypal"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${payTab === "paypal" ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-slate-200 hover:border-brand-navy"}`}>
                          <CreditCard className="w-4 h-4" /> PayPal / Card
                        </button>
                        <button onClick={() => setPayTab("qr")} data-testid="pay-tab-qr"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${payTab === "qr" ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-slate-200 hover:border-brand-navy"}`}>
                          <QrCode className="w-4 h-4" /> QR Pay (VietQR)
                        </button>
                      </div>
                      {payTab === "paypal" ? (
                        config?.paypal_client_id ? (
                          <PayPalScriptProvider options={{ clientId: config.paypal_client_id, currency: "USD", intent: "capture" }}>
                            <PayPalButtons style={{ layout: "vertical", color: "gold", shape: "pill" }}
                              createOrder={(d, actions) => actions.order.create({
                                purchase_units: [{ amount: { value: Number(app.quotation.amount).toFixed(2), currency_code: "USD" }, description: `${svc?.title} — ${app.reference_no}` }],
                              })}
                              onApprove={async (d, actions) => { const details = await actions.order.capture(); await onPaid(details); }}
                              onError={() => toast.error("Payment could not be completed. Please try again.")} />
                          </PayPalScriptProvider>
                        ) : <p className="text-sm text-slate-500">Online payment is being configured. Please contact us to complete payment.</p>
                      ) : (
                        <div data-testid="qr-pay-panel">
                          <p className="text-sm text-slate-600 mb-3">Scan the QR with any VietQR / napas247 banking app and transfer <strong>USD {Number(app.quotation.amount).toFixed(2)}</strong> (or the VND equivalent). Add reference <strong className="text-brand-navy">{app.reference_no}</strong> to your transfer note, then upload your payment confirmation below.</p>
                          <div className="flex flex-col sm:flex-row gap-5 items-start">
                            <img src="/immilaw-pay-qr.jpeg" alt="VietQR napas247 payment QR" data-testid="qr-image"
                              className="w-44 h-44 rounded-xl border border-slate-200 bg-white object-contain p-2 shrink-0" />
                            <div className="flex-1 w-full">
                              <input ref={proofRef} type="file" className="hidden" accept="image/*,application/pdf" onChange={onProofSelected} data-testid="qr-proof-input" />
                              <div className="flex items-center gap-3 flex-wrap">
                                <button type="button" onClick={() => proofRef.current?.click()} disabled={uploadingProof} data-testid="qr-proof-upload-btn"
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-semibold text-brand-navy hover:border-brand-red hover:text-brand-red transition-colors">
                                  {uploadingProof ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {qrProof ? "Replace proof" : "Upload payment proof"}
                                </button>
                                {qrProof && <span className="text-xs text-emerald-600 font-medium truncate max-w-[200px] flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {qrProof.filename}</span>}
                              </div>
                              <textarea value={qrNote} onChange={(e) => setQrNote(e.target.value)} rows={2} placeholder="Note (optional) — e.g. sender name / bank" data-testid="qr-note"
                                className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-red resize-none" />
                              <button onClick={submitClaim} disabled={submittingClaim || !qrProof} data-testid="submit-claim-btn"
                                className="mt-3 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors shadow-redglow disabled:opacity-60">
                                {submittingClaim ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />} I've paid — submit proof
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {app.payment && (
                <div className="px-6 py-4 bg-emerald-50 border-t border-emerald-100 flex items-center gap-2 text-sm text-emerald-700 font-semibold" data-testid="paid-banner">
                  <CheckCircle2 className="w-4 h-4" /> Paid — USD {Number(app.payment.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} · Receipt {app.receipt?.receipt_no}
                </div>
              )}
            </div>
          )}

          {/* Application result (both roles) */}
          {app.result && (
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="result-panel">
              <div className="px-6 py-4 border-b border-slate-100 font-semibold text-brand-navy flex items-center gap-2"><Award className="w-4 h-4 text-brand-red" /> Application result</div>
              <div className="px-6 py-4">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${OUTCOME_CLS[app.result.outcome] || OUTCOME_CLS.approved}`} data-testid="result-outcome-pill">{OUTCOME_LABEL[app.result.outcome] || app.result.outcome}</span>
                {app.result.summary && <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap">{app.result.summary}</p>}
                <div className="mt-4 flex flex-wrap gap-3">
                  <DownloadBtn href={fileUrl(`applications/${id}/result.pdf`)} label="Result letter (PDF)" icon={FileText} testid="result-letter-btn" />
                  {app.result.result_file_path && <DownloadBtn href={fileUrl(`applications/${id}/result-file`)} label={app.result.result_filename || "Issued document"} icon={FileCheck2} testid="result-file-btn" />}
                </div>
              </div>
            </div>
          )}

          {/* Admin: QR payment confirmation */}
          {isAdmin && app.payment_claim?.status === "pending" && !app.payment && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="qr-confirm-panel">
              <h3 className="font-semibold text-brand-navy flex items-center gap-2"><QrCode className="w-4 h-4 text-brand-red" /> QR payment awaiting confirmation</h3>
              <p className="text-sm text-slate-600 mt-1">The applicant submitted a QR / bank-transfer proof{app.payment_claim.note ? ` — "${app.payment_claim.note}"` : ""}. Verify the transfer of USD {Number(app.payment_claim.amount).toFixed(2)}, then confirm.</p>
              <div className="mt-3 flex flex-wrap gap-3">
                {app.payment_claim.proof_path && <DownloadBtn href={fileUrl(`files/${app.payment_claim.proof_path}`)} label={app.payment_claim.proof_filename || "Payment proof"} icon={Paperclip} testid="qr-proof-btn" />}
                <button onClick={confirmQr} disabled={confirmingQr} data-testid="confirm-qr-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-60">
                  {confirmingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirm payment received
                </button>
              </div>
            </div>
          )}

          {/* Admin controls */}
          {isAdmin && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-6" data-testid="admin-controls">
              <div>
                <h3 className="font-semibold text-brand-navy mb-3 flex items-center justify-between">
                  <span>{app.quotation ? "Update quotation" : "Generate quotation"}</span>
                  {app.service_type === "overstay_report" && app.form_data?.overstay_fee_usd && (
                    <button onClick={prefillOverstay} data-testid="prefill-overstay-btn" className="text-xs font-semibold text-brand-red hover:underline">Pre-fill overstay fee</button>
                  )}
                </h3>
                <div className="space-y-2">
                  {items.map((it, i) => (
                    <div key={i} className="flex gap-2">
                      <input placeholder="Description" value={it.description} onChange={(e) => setItems((a) => a.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                        className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-red" data-testid={`quote-item-desc-${i}`} />
                      <input placeholder="USD" type="number" value={it.amount} onChange={(e) => setItems((a) => a.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                        className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-red" data-testid={`quote-item-amount-${i}`} />
                      <button onClick={() => setItems((a) => a.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-brand-red"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => setItems((a) => [...a, { description: "", amount: "" }])} className="mt-2 inline-flex items-center gap-1.5 text-sm text-brand-red font-semibold"><Plus className="w-4 h-4" /> Add line</button>
                <textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                  className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-red resize-none" data-testid="quote-notes" />
                <button onClick={saveQuote} disabled={savingQuote} data-testid="issue-quotation-btn"
                  className="mt-3 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors disabled:opacity-60">
                  {savingQuote ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Issue quotation
                </button>
              </div>
              <div className="border-t border-slate-100 pt-5">
                <h3 className="font-semibold text-brand-navy mb-3">Update status</h3>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(s)} data-testid={`set-status-${s}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${app.status === s ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-slate-200 hover:border-brand-navy"}`}>
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-t border-slate-100 pt-5">
                <h3 className="font-semibold text-brand-navy mb-3">Action flag</h3>
                <button onClick={toggleFlag} disabled={flagging} data-testid="flag-action-btn"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${app.needs_action ? "bg-red-100 text-brand-red border-red-200 hover:bg-red-200" : "bg-white text-slate-600 border-slate-200 hover:border-brand-red hover:text-brand-red"}`}>
                  {flagging ? <Loader2 className="w-4 h-4 animate-spin" /> : app.needs_action ? <Flag className="w-4 h-4 fill-current" /> : <Flag className="w-4 h-4" />}
                  {app.needs_action ? "Clear action flag" : "Flag: Action needed"}
                </button>
                <p className="mt-2 text-xs text-slate-400">Flagged applications stand out with an "Action" badge on the applications list.</p>
              </div>
              <div className="border-t border-slate-100 pt-5">
                <h3 className="font-semibold text-brand-navy mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-brand-red" /> {app.result ? "Update result" : "Issue result"}</h3>
                <label className="text-xs uppercase tracking-wide text-slate-500 font-bold">Outcome</label>
                <select value={resultForm.outcome} onChange={(e) => setResultForm((r) => ({ ...r, outcome: e.target.value }))} data-testid="result-outcome"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-red">
                  {OUTCOMES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <textarea value={resultForm.summary} onChange={(e) => setResultForm((r) => ({ ...r, summary: e.target.value }))} rows={3}
                  placeholder="Result summary / decision details shown to the applicant…" data-testid="result-summary"
                  className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-red resize-none" />
                <input ref={resultRef} type="file" className="hidden" accept="image/*,application/pdf" onChange={onResultFileSelected} data-testid="result-file-input" />
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button type="button" onClick={() => resultRef.current?.click()} disabled={uploadingResult} data-testid="result-upload-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold text-brand-navy hover:border-brand-red hover:text-brand-red transition-colors">
                    {uploadingResult ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {resultFile ? "Replace document" : "Attach issued document"}
                  </button>
                  {resultFile && <span className="text-xs text-slate-500 truncate max-w-[220px]">{resultFile.filename}</span>}
                </div>
                <button onClick={issueResult} disabled={issuingResult} data-testid="issue-result-btn"
                  className="mt-3 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors disabled:opacity-60">
                  {issuingResult ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />} {app.result ? "Update result & re-email" : "Issue result & email applicant"}
                </button>
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-notes">
              <h3 className="font-semibold text-brand-navy flex items-center gap-2"><Lock className="w-4 h-4 text-brand-red" /> Internal notes <span className="text-xs font-normal text-slate-400">(private — never shown to the applicant)</span></h3>
              <div className="mt-3 space-y-2">
                {adminNotes.length === 0 ? <p className="text-sm text-slate-400">No internal notes yet.</p> : adminNotes.map((n) => (
                  <div key={n.id} className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <p className="text-sm text-brand-navy whitespace-pre-wrap">{n.text}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{n.author} · {(n.created_at || "").slice(0, 10)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a private note…" data-testid="admin-note-input"
                  className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-red" />
                <button onClick={addNote} data-testid="admin-note-add" className="px-4 py-2 rounded-full bg-brand-navy text-white text-sm font-semibold hover:bg-slate-800 transition-colors">Add</button>
              </div>
            </div>
          )}
        </div>

        {/* Right: messaging */}
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-slate-200 bg-white flex flex-col h-[520px] sticky top-28" data-testid="messages-panel">
            <div className="px-5 py-4 border-b border-slate-100 font-semibold text-brand-navy flex items-center gap-2"><MessageSquare className="w-4 h-4 text-brand-red" /> Messages {isAdmin ? "with applicant" : "with our team"}</div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? (
                <p className="text-sm text-slate-400 text-center mt-8">No messages yet. Send a message about this application.</p>
              ) : messages.map((m) => {
                const mine = m.sender_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-brand-red text-white" : "bg-slate-100 text-brand-navy"}`}>
                      <p className="text-[10px] opacity-70 mb-0.5">{m.sender_role === "admin" ? "ImmiLaw Team" : m.sender_name || "You"}</p>
                      {m.text}
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={sendMessage} className="p-3 border-t border-slate-100 flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" data-testid="message-input"
                className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand-red" />
              <button type="submit" disabled={sending} data-testid="send-message-btn"
                className="w-11 h-11 rounded-full bg-brand-red text-white flex items-center justify-center hover:bg-brand-reddark transition-colors disabled:opacity-60">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
